import React, { useState, useRef } from 'react';
import { Card, Btn, SectionTitle, Divider } from '../components/UI';

const PLANT_JOURNAL_KEY = 'plant-journal-v4';
const APP_STORE_KEY = 'koo-yong-v1';

function migrateFromPlantJournal(raw) {
  // plant-journal-v4 format: { plants: [...] }
  // Each plant may have: id, name, species, emoji, waterEvery, lastWatered, logs
  try {
    const data = JSON.parse(raw);
    const plants = (data.plants || []).map(p => ({
      id: p.id || ('p_' + Date.now() + Math.random()),
      name: p.name || 'Unknown',
      variety: p.species || '',
      type: 'Vegetable',
      status: 'Growing',
      location: '',
      sowDate: '',
      waterEvery: p.waterEvery ? String(p.waterEvery) : '',
      lastWatered: p.lastWatered || null,
      notes: '',
      image: null,
      careLog: (p.logs || []).map(l => ({
        id: l.id || ('c_' + Math.random()),
        type: l.type === 'watered' ? 'Watered'
          : l.type === 'fertilised' ? 'Fertilised'
          : l.type === 'repotted' ? 'Repotted'
          : l.type === 'pruned' ? 'Pruned'
          : 'Observed',
        date: l.date || '',
        note: l.note || '',
      })),
      harvestLog: [],
      createdAt: new Date().toISOString(),
    }));
    return plants;
  } catch (e) {
    return null;
  }
}

export default function Settings({ state, dispatch }) {
  const [importStatus, setImportStatus] = useState('');
  const [migrateStatus, setMigrateStatus] = useState('');
  const fileRef = useRef(null);

  // Check if old plant journal data exists in this browser
  const oldJournalRaw = (() => {
    try { return localStorage.getItem(PLANT_JOURNAL_KEY); } catch { return null; }
  })();

  function handleExport() {
    const json = JSON.stringify(state, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'koo-yong-backup.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleImportFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        // Validate it looks like our data
        if (typeof data !== 'object') throw new Error('Invalid');
        dispatch({ type: 'IMPORT_DATA', data });
        setImportStatus('✓ Data imported successfully');
      } catch (err) {
        setImportStatus('✗ Could not read file — make sure it\'s a Koo Yong backup JSON');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleMigrateFromOldJournal() {
    if (!oldJournalRaw) return;
    const plants = migrateFromPlantJournal(oldJournalRaw);
    if (!plants || plants.length === 0) {
      setMigrateStatus('✗ Could not read old journal data');
      return;
    }
    // Add each plant via ADD_PLANT so upsert logic applies
    plants.forEach(plant => dispatch({ type: 'ADD_PLANT', plant }));
    setMigrateStatus(`✓ Imported ${plants.length} plant${plants.length !== 1 ? 's' : ''} from your old journal`);
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 600 }} className="fade-up">
      <h1 style={{ fontSize: 24, marginBottom: '0.25rem' }}>Settings</h1>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '2rem' }}>Manage your data</p>

      {/* Migrate from old Claude artifact journal */}
      {oldJournalRaw && (
        <Card style={{ marginBottom: '1rem', borderColor: 'var(--moss-200)' }}>
          <SectionTitle>Migrate from Claude plant journal</SectionTitle>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
            We found your existing plant journal data (plant-journal-v4) in this browser. Click below to import it into Koo Yong.
          </p>
          {migrateStatus ? (
            <div style={{ fontSize: 13, color: migrateStatus.startsWith('✓') ? 'var(--moss-600)' : 'var(--red-600)', marginBottom: '0.5rem' }}>{migrateStatus}</div>
          ) : (
            <Btn variant="primary" onClick={handleMigrateFromOldJournal}>Import existing plants</Btn>
          )}
        </Card>
      )}

      <Card style={{ marginBottom: '1rem' }}>
        <SectionTitle>Export data</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
          Download a full backup of all your Koo Yong data — plants, costs, tasks, journal entries and tree map. Use this to move data between devices or browsers.
        </p>
        <Btn variant="primary" onClick={handleExport}>↓ Download backup</Btn>
      </Card>

      <Card style={{ marginBottom: '1rem' }}>
        <SectionTitle>Import data</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
          Restore from a previously exported Koo Yong backup file. This will merge the imported data with your current data.
        </p>
        {importStatus && (
          <div style={{ fontSize: 13, color: importStatus.startsWith('✓') ? 'var(--moss-600)' : 'var(--red-600)', marginBottom: '0.75rem' }}>{importStatus}</div>
        )}
        <Btn onClick={() => fileRef.current?.click()}>↑ Choose backup file</Btn>
        <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImportFile} />
      </Card>

      <Card>
        <SectionTitle>How to move data to this site</SectionTitle>
        <ol style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 2, paddingLeft: '1.25rem' }}>
          <li>Open the Claude conversation where you originally used the plant journal</li>
          <li>In that chat, open the plant journal artifact</li>
          <li>The artifact should have an export option — download the JSON file</li>
          <li>Come back here and use <strong>Import data</strong> above to load it in</li>
        </ol>
        <Divider />
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
          If you're on the same device and browser where you used the Claude artifact, the <strong>Migrate from Claude plant journal</strong> option above will appear and do this automatically.
        </p>
      </Card>
    </div>
  );
}
